import React, { useState } from 'react';
import { Gift, CheckCircle2, Clock, DollarSign, CircleDashed, Ban, Eye } from 'lucide-react';
import { useSignupBonusGrants, useSignupBonusKpi } from '../../../hooks/useApi';
import { SignupBonusStatus } from '../../../types';
import { Card, CardContent } from '../../ui/Card';
import Button from '../../ui/Button';
import { LoadingSpinner } from '../../ui/Loading';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { idleWindowLabel, SIGNUP_BONUS_STATUS_LABEL } from './signupBonusDisplay';

const STATUS_OPTIONS: Array<{ value: SignupBonusStatus | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'active', label: 'Active' },
  { value: 'in_review', label: 'In review' },
  { value: 'completed', label: 'Completed' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_BADGE: Record<SignupBonusStatus, { label: string; className: string }> = {
  inactive: { label: SIGNUP_BONUS_STATUS_LABEL.inactive, className: 'bg-violet-100 text-violet-700' },
  active: { label: SIGNUP_BONUS_STATUS_LABEL.active, className: 'bg-emerald-100 text-emerald-700' },
  in_review: { label: SIGNUP_BONUS_STATUS_LABEL.in_review, className: 'bg-amber-100 text-amber-800' },
  completed: { label: SIGNUP_BONUS_STATUS_LABEL.completed, className: 'bg-sky-100 text-sky-700' },
  expired: { label: SIGNUP_BONUS_STATUS_LABEL.expired, className: 'bg-slate-100 text-slate-600' },
};

const usd = (v: string | number | null | undefined) => `$${(Number(v) || 0).toFixed(2)}`;

const GrantsList: React.FC = () => {
  const fmt = useFormatDate();
  const [status, setStatus] = useState<SignupBonusStatus | ''>('');
  const [shopId, setShopId] = useState('');
  const [page, setPage] = useState(1);

  const filters = { status, shopId: shopId || undefined, page, limit: 20 };
  const { data, isLoading, error } = useSignupBonusGrants(filters);
  const { data: kpiData } = useSignupBonusKpi();

  const kpi = kpiData?.data;
  const grants = data?.data.grants ?? [];
  const pagination = data?.data.pagination;
  const by = kpi?.byStatus;

  return (
    <div className="space-y-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Gift className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-lg font-semibold">{kpi?.totalGrants ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-violet-100 rounded-lg"><CircleDashed className="h-5 w-5 text-violet-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Inactive</p>
              <p className="text-lg font-semibold">{by?.inactive ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-emerald-50 rounded-lg"><Clock className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Active</p>
              <p className="text-lg font-semibold">{by?.active ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-amber-100 rounded-lg"><Eye className="h-5 w-5 text-amber-700" /></div>
            <div>
              <p className="text-xs text-muted-foreground">In review</p>
              <p className="text-lg font-semibold">{by?.in_review ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-sky-100 rounded-lg"><CheckCircle2 className="h-5 w-5 text-sky-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Completed</p>
              <p className="text-lg font-semibold">{by?.completed ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-slate-100 rounded-lg"><Ban className="h-5 w-5 text-slate-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Expired</p>
              <p className="text-lg font-semibold">{by?.expired ?? '—'}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg"><DollarSign className="h-5 w-5 text-purple-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Promo cost</p>
              <p className="text-lg font-semibold">{usd(kpi?.promoCostUsd)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Status</label>
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={status}
                onChange={(e) => { setStatus(e.target.value as SignupBonusStatus | ''); setPage(1); }}
              >
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Shop ID</label>
              <input
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                placeholder="Filter by shop UUID"
                value={shopId}
                onChange={(e) => { setShopId(e.target.value.trim()); setPage(1); }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-48"><LoadingSpinner /></div>
          ) : error ? (
            <div className="p-6 text-destructive text-sm">
              {error instanceof Error ? error.message : 'Failed to load grants.'}
            </div>
          ) : grants.length === 0 ? (
            <div className="p-6 text-muted-foreground text-sm">No welcome bonus grants found.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3">Player</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Grant</th>
                  <th className="px-4 py-3">Event peak / Threshold</th>
                  <th className="px-4 py-3">Real $</th>
                  <th className="px-4 py-3">Funding</th>
                  <th className="px-4 py-3">Claimed</th>
                  <th className="px-4 py-3">Inactivity rule</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((g) => (
                  <tr key={g.id} className="border-b last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-medium">{g.user?.username || g.user?.email || g.userId.slice(0, 8)}</div>
                      {g.user?.email && <div className="text-xs text-muted-foreground">{g.user.email}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[g.status]?.className || ''}`}>
                        {STATUS_BADGE[g.status]?.label || g.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">{usd(g.grantedAmount)}</td>
                    <td className="px-4 py-3">{usd(g.peakNetWin)} / {usd(g.winThreshold)}</td>
                    <td className="px-4 py-3">{usd(g.realAmount)}</td>
                    <td className="px-4 py-3 capitalize">
                      {g.fundingSource === 'shop' ? 'Shop credits' : g.fundingSource === 'platform' ? 'Platform credits' : (g.fundingSource || '—')}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{g.grantedAt ? fmt(g.grantedAt) : '—'}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {g.status === 'expired' && g.expiredAt
                        ? `Expired ${fmt(g.expiredAt)}`
                        : idleWindowLabel(g.grantedAt, g.expiresAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Page {pagination.page} of {pagination.pages} · {pagination.total} total
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= pagination.pages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrantsList;
